import { useCallback, useState, useEffect, useRef } from "react";
import { speakText, stopSpeaking } from "@/utils/speechPlayer";
import { normalizeLanguageKey } from "@/constants/languageMap";
import useStore from "@/store";

const useSpeechSynthesis = (sentences, onEndOfSentences) => {
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [activeSentence, setActiveSentence] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [rate, setRate] = useState(1);

  const { currentCollection } = useStore().collections;
  const collectionLanguage = currentCollection?.language || "en";
  const normalizedCollectionLanguage = normalizeLanguageKey(collectionLanguage);
  const playbackLanguage =
    normalizedCollectionLanguage?.replace(/_/g, "-") || collectionLanguage;

  const audioRef = useRef(null);
  const isListeningRef = useRef(isListening);
  const rateRef = useRef(rate);
  const sentencesRef = useRef(sentences);
  const playbackLanguageRef = useRef(playbackLanguage);
  // Index of the sentence whose playback was last started, so the effect below
  // doesn't start it a second time after startListening already did.
  const startedIndexRef = useRef(null);

  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);

  useEffect(() => {
    rateRef.current = rate;
  }, [rate]);

  useEffect(() => {
    sentencesRef.current = sentences;
  }, [sentences]);

  useEffect(() => {
    playbackLanguageRef.current = playbackLanguage;
  }, [playbackLanguage]);

  const cleanupAudio = useCallback(() => {
    stopSpeaking();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setIsGenerating(false);
  }, []);

  const playSentence = useCallback(
    async (index) => {
      const sentencesList = sentencesRef.current || [];
      if (sentencesList.length === 0) {
        setIsListening(false);
        setCurrentSentenceIndex(0);
        onEndOfSentences?.();
        return;
      }

      if (!isListeningRef.current) {
        return;
      }

      if (index >= sentencesList.length) {
        setIsListening(false);
        setCurrentSentenceIndex(0);
        onEndOfSentences?.();
        return;
      }

      startedIndexRef.current = index;
      const sentence = sentencesList[index];
      if (
        !sentence ||
        typeof sentence !== "string" ||
        sentence.trim().length === 0
      ) {
        setCurrentSentenceIndex(index + 1);
        return;
      }

      setActiveSentence(sentence);

      try {
        setIsGenerating(true);
        console.log('the sentence to speak:', sentence);
        const audio = await speakText(sentence, playbackLanguageRef.current, {
          speed: rateRef.current,
          onEnded: () => {
            if (!isListeningRef.current) {
              return;
            }
            const nextIndex = index + 1;
            if (nextIndex < (sentencesRef.current || []).length) {
              setCurrentSentenceIndex(nextIndex);
            } else {
              setIsListening(false);
              setCurrentSentenceIndex(0);
              onEndOfSentences?.();
            }
          },
          onError: () => {
            setIsListening(false);
          },
        });

        if (!audio) {
          setIsListening(false);
          return;
        }

        audioRef.current = audio;
      } catch (error) {
        console.error("Failed to play sentence with TTS:", error);
        setIsListening(false);
      } finally {
        setIsGenerating(false);
      }
    },
    [onEndOfSentences]
  );

  useEffect(() => {
    if (isListening) {
      if (startedIndexRef.current !== currentSentenceIndex) {
        playSentence(currentSentenceIndex);
      }
    } else {
      startedIndexRef.current = null;
      cleanupAudio();
    }
  }, [isListening, currentSentenceIndex, playSentence, cleanupAudio]);

  useEffect(
    () => () => {
      cleanupAudio();
    },
    [cleanupAudio]
  );

  const startListening = (playbackRate = 1) => {
    const clampedRate = Math.max(0.5, Math.min(playbackRate, 2));
    rateRef.current = clampedRate;
    isListeningRef.current = true;
    startedIndexRef.current = null;
    setRate(clampedRate);
    setCurrentSentenceIndex(0);
    setIsListening(true);
    // Fire immediately in the same user gesture to avoid autoplay blocks
    playSentence(0);
  };

  const stopListening = () => {
    isListeningRef.current = false;
    setIsListening(false);
  };

  return {
    activeSentence,
    isListening,
    isGenerating,
    startListening,
    stopListening,
  };
};

export default useSpeechSynthesis;
